import React, { useEffect, useState } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import { Button, PageTitle, useFetch, PseudoStates, Sticker, useFeatures, useSession } from '@cordata/buffet';
import {
  Alert,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import {
  baseInstance as Base,
  adminInstance as admin,
  toastSuccess,
  toastError,
} from '~/utils';
import { BaseButton } from '~/components/common';

import UserForm from '~/components/User/Form';
import ChangePasswordForm from '~/components/User/ChangePasswordForm';
import RevealActivationCodeModal from '~/components/User/RevealActivationCodeModal';
import { sendActivationEmail } from './collection';

const UserDetailsPageTitleAction = ({
  user,
}) => {
  const [isButtonDropdownOpen, setIsButtonDropdownOpen] = useState(false);
  const [isOnActivationCooldown, setIsOnActivationCooldown] = useState(false);
  const { hasAppKickstarter } = useFeatures();
  const { current } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [isRevealOpen, setIsRevealOpen] = useState(false);
  const isViewingSelf = user?.appUser.id === current.attributes.userUid;

  const toggleIsOpen = () =>
    setIsOpen(p => !p);

  const toggleRevealOpen = () =>
    setIsRevealOpen(p => !p);

  const toggleButtonDropdownOpen = () =>
    setIsButtonDropdownOpen(p => !p);

  // Don't show actions when viewing yourself and you're not a kickstarter user
  if (isViewingSelf && !hasAppKickstarter) {
    return null;
  }

  // forgot password until people say "I want a different email template"
  const forgotPassword = () =>
    Base
      .post('/forgot-password', {
        'user-name-or-email': user.appUser.userName,
      })
      .then(() => toastSuccess('Successfully sent forgot password email!'))
      .catch(toastError);

  const toggleAccount = (skipConfirm = false) => {
    /* eslint-disable-next-line no-restricted-globals */
    const p = user.appUser.isInSelections
      ? {
        'is-in-selections': false,
        'account-is-locked': true,
        'can-impersonate': false,
        'mfa-required': user.appUser.mfaRequired,
        'password-reset-required': true,
      } : {
        'is-in-selections': true,
        'account-is-locked': false,
        'password-reset-required': true,
        'can-impersonate': false,
        'mfa-required': user.appUser.mfaRequired,
      };

    admin
      .put(`/app-user/${user.appUser.id}/account`, p)
      .then(() => {
        if (!skipConfirm) {
          toastSuccess(
            `${user.appUser.isInSelections ? 'Deactivated' : 'Activated'}! Reloading page.`,
            { onClose: () => window.location.reload() },
          );
        }
      });
  };

  const doToggle = () => {
    // calling activateAccount directly from the onClick passes in an event
    //   for the parameter `skipConfirm`, which is truthy. This avoids that issue.
    toggleAccount(false);
  };

  const sendActivation = () => {
    const toastActiveTime = 5000;
    if (!user.appUser.isInSelections) {
      toggleAccount(true);
    }
    sendActivationEmail(
      user.appUser.userName,
      localStorage.getItem('org')?.slice(-6),
      user.appUser.mobilePhone,
    );
    setIsOnActivationCooldown(true);

    // disable button as long as the toast is active.
    setTimeout(() => {
      setIsOnActivationCooldown(false);
    }, toastActiveTime);
  };

  if (user.appUser.authenticationService !== 'cordata') {
    return (
      <Button outline onClick={doToggle}>
        {user.appUser.isInSelections ? 'Deactivate' : 'Activate'} Account
      </Button>
    );
  }

  const hasLoggedIn = !!user.appUser.lastLoginDate;

  return (
    <React.Fragment>
      <ButtonDropdown
        size="sm"
        isOpen={isButtonDropdownOpen}
        toggle={toggleButtonDropdownOpen}
      >
        {hasLoggedIn && <BaseButton onClick={forgotPassword} outline icon="unlock" text="Reset Password" />}
        {!hasLoggedIn && <BaseButton onClick={sendActivation} outline icon="key" text="Send Activation" disabled={isOnActivationCooldown} />}

        <DropdownToggle outline caret />

        <DropdownMenu right>
          {hasLoggedIn &&
            <DropdownItem onClick={sendActivation}>Send Activation</DropdownItem>}

          {!hasLoggedIn &&
            <DropdownItem onClick={forgotPassword}>Reset Password</DropdownItem>}

          {hasAppKickstarter && (
            <DropdownItem onClick={toggleIsOpen}>
              Change Password
            </DropdownItem>
          )}

          <DropdownItem
            onClick={doToggle}
          >
            {user.appUser.isInSelections ? 'Deactivate' : 'Activate'} Account
          </DropdownItem>

          {hasAppKickstarter && (
            <DropdownItem onClick={toggleRevealOpen}>
              Resend activation with revealed code
            </DropdownItem>
          )}

          {user.provider.id && (
            <>
              <DropdownItem divider />

              <DropdownItem
                tag={Link}
                to={`/admin/providers/${user.provider.id}`}
                color="primary"
              >
                <Sticker icon="external-link-alt">
                  Go to provider record
                </Sticker>
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </ButtonDropdown>

      <ChangePasswordForm toggle={toggleIsOpen} isOpen={isOpen} appUser={user.appUser} />
      <RevealActivationCodeModal
        isOpen={isRevealOpen}
        toggle={toggleRevealOpen}
        userUid={user.appUser.id}
      />
    </React.Fragment>
  );
};

UserDetailsPageTitleAction.propTypes = {
  user: PropTypes.shape({
    appUser: PropTypes.shape().isRequired,
    provider: PropTypes.shape(),
  }).isRequired,
};

const UserDetails = () => {
  const { user_id: userId } = useParams();
  const history = useHistory();
  const { current } = useSession();
  const { hasAppKickstarter } = useFeatures();
  const [status, user, meta] = useFetch(`admin/app-user-2/${userId}/__schema__`, {
    params: {
      'org-uid': localStorage.getItem('org'),
    },
    instance: 'p',
  });
  const [refresh, setRefresh] = useState(false);
  const isViewingSelf = user?.appUser.id === current.attributes.userUid;
  const canEdit = !(isViewingSelf && !hasAppKickstarter);

  useEffect(() => {
    if (refresh && user) {
      setRefresh(false);
    }
  }, [refresh]);

  if (status === 'loading') {
    return (
      <>
        <PageTitle>Loading user details ...</PageTitle>
        <PseudoStates status={status} />
      </>
    );
  }

  if (status === 'error') {
    return (
      <>
        <PageTitle>Error loading user details</PageTitle>
        <PseudoStates status={status} />
      </>
    );
  }

  const unlockUser = () =>
    admin
      .put(`/app-user/${user.appUser.id}/unlock`)
      .then(() => {
        toastSuccess('Successfully unlocked!');
        history.push('/admin/users');
      })
      .catch(toastError);

  return (
    <>
      <PageTitle
        action={
          <UserDetailsPageTitleAction
            appUser={user.appUser}
            user={user}
          />
        }
      >
        {user.appUser.displayName}
      </PageTitle>

      {user.appUser.accountIsLocked && (
        <Alert color="info" className="d-flex">
          <div className="mr-auto">Account is locked at {format(user.appUser.updatedDate, 'ddd MMM Do YYYY [@] h:mm A')}</div>
          {!isViewingSelf && (
            <>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <div className="ml-auto"><small><a href="#" className="alert-link" onClick={unlockUser}>Unlock User</a></small></div>
            </>
          )}
        </Alert>
      )}

      <UserForm user={user} meta={meta} canEdit={canEdit} />

      <p className="text-muted pt-3">
        <em>
          Bad password count: <strong>{user.appUser.badPasswordCount}</strong> | Last login date: <strong>{user.appUser.lastLoginDate ? format(user.appUser.lastLoginDate, 'ddd MMM Do YYYY [@] h:mm A') : 'Never'}</strong>
        </em>
      </p>
    </>
  );
};

export default UserDetails;
